import React, { useCallback, useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import './autocomplete.module.css';
import { Patient } from 'types/patient';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import PatientItem from './PatientItem';

let timer: NodeJS.Timeout;

interface AutocompleteFromProps {
  onSelect(value: Patient | null): void;
  label: string;
  placeholder: string;
  selected: Patient | null;
  helperText?: string;
  hideHint?: boolean;
  popupIcon?: React.ReactNode;
  error?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

const PatientsAutoComplete: React.FC<AutocompleteFromProps> = ({
  onSelect,
  label,
  placeholder,
  selected,
  helperText = '',
  hideHint = false,
  popupIcon,
  error = false,
  ref,
}) => {
  const [loading, setLoading] = useState(false);
  const [filtered, setFiltered] = useState<Patient[]>([]);
  const [options, setOptions] = useState<Patient[]>([]);

  const handleSelect = useCallback(
    (option: Patient) => {
      onSelect(option);
      setFiltered([]);
    },
    [onSelect],
  );

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  const findPatients = useCallback(async (term: string) => {
    setLoading(true);
    api
      .get('/patients/all', { params: { name: term, active: 1 } })
      .then(response => {
        setOptions(response.data);
      })
      .catch(err => {
        toast.error('Ocorreu um erro ao buscar os pacientes');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  function handleChange(value: string) {
    clearTimeout(timer);

    if (value.length < 2) {
      return;
    }

    timer = setTimeout(() => findPatients(value), 400);
  }

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      handleHomeEndKeys
      openOnFocus
      {...(popupIcon ? { popupIcon } : {})}
      value={selected}
      options={filtered}
      noOptionsText="Nenhum paciente encontrado"
      getOptionLabel={option => option.name}
      onChange={(event, newValue) => onSelect(newValue)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <PatientItem handleSelect={handleSelect} option={option} selected={selected} />
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={ref}
          onBlur={() => setFiltered([])}
          onChange={e => handleChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText || (hideHint ? undefined : 'Digite 2 ou mais caracteres')}
          fullWidth
        />
      )}
    />
  );
};

export default PatientsAutoComplete;
